import { Badge, Box, HStack, Icon, Input, Text } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import {
  FiAlertCircle,
  FiCheck,
  FiHash,
  FiLink,
  FiLoader
} from "react-icons/fi";
import { mockApi } from "../../api/mockApi";

interface LinkConfigurationProps {
  redirectUrl: string;
  customId: string;
  onRedirectUrlChange: (value: string) => void;
  onCustomIdChange: (value: string, isAvailable: boolean | null) => void;
  disabled?: boolean;
}

export const LinkConfiguration: React.FC<LinkConfigurationProps> = ({
  customId,
  onCustomIdChange,
  disabled = false
}) => {
  const [customIdAvailable, setCustomIdAvailable] = useState<boolean | null>(
    null
  );
  const [checkingCustomId, setCheckingCustomId] = useState(false);
  const customIdTimeoutRef = React.useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);

  useEffect(() => {
    return () => {
      if (customIdTimeoutRef.current) {
        clearTimeout(customIdTimeoutRef.current);
      }
    };
  }, []);

  const validateCustomId = (customId: string): string | null => {
    if (!customId) return null;

    // Only allow letters, numbers, and hyphens
    if (!/^[a-zA-Z0-9-]+$/.test(customId)) {
      return "Only letters, numbers, and hyphens are allowed";
    }

    // Must start and end with letter or number
    if (!/^[a-zA-Z0-9].*[a-zA-Z0-9]$/.test(customId)) {
      return "Must start and end with a letter or number";
    }

    // Check length
    if (customId.length < 3) {
      return "Must be at least 3 characters long";
    }

    if (customId.length > 50) {
      return "Must be less than 50 characters";
    }

    return null;
  };

  const checkCustomIdAvailability = async (customId: string) => {
    if (!customId) {
      setCustomIdAvailable(null);
      return;
    }

    const error = validateCustomId(customId);
    if (error) {
      setCustomIdAvailable(false);
      onCustomIdChange(customId, false);
      return;
    }

    setCheckingCustomId(true);
    try {
      const isAvailable = await mockApi.checkCustomIdAvailability(customId);
      setCustomIdAvailable(isAvailable);
      onCustomIdChange(customId, isAvailable);
    } catch (error) {
      console.error("Error checking custom ID:", error);
      setCustomIdAvailable(false);
      onCustomIdChange(customId, false);
    } finally {
      setCheckingCustomId(false);
    }
  };

  const handleCustomIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onCustomIdChange(value, null);
    setCustomIdAvailable(null);

    if (customIdTimeoutRef.current) {
      clearTimeout(customIdTimeoutRef.current);
    }

    if (value) {
      customIdTimeoutRef.current = setTimeout(() => {
        checkCustomIdAvailability(value);
      }, 500);
    }
  };

  return (
    <Box bg="white" shadow="sm" rounded="lg" p={6}>
      <HStack mb={4}>
        <Icon as={FiLink} color="blue.500" boxSize={5} />
        <Text fontWeight="semibold" fontSize="lg">
          Link Configuration
        </Text>
      </HStack>

      <Box>
        <HStack mb={2}>
          <Icon as={FiHash} color="gray.500" boxSize={4} />
          <Text fontWeight="medium">Custom URL ID</Text>
          <Badge colorScheme="gray" size="sm">
            Optional
          </Badge>
        </HStack>
        <Box position="relative">
          <Input
            name="customId"
            value={customId}
            onChange={handleCustomIdChange}
            placeholder="e.g., summer-sale-2024"
            size="lg"
            disabled={disabled}
            pr={10}
            borderColor={
              customId && customIdAvailable !== null
                ? customIdAvailable
                  ? "green.500"
                  : "red.500"
                : undefined
            }
            _focus={{
              borderColor:
                customId && customIdAvailable !== null
                  ? customIdAvailable
                    ? "green.500"
                    : "red.500"
                  : "blue.500",
              boxShadow: `0 0 0 1px var(--chakra-colors-${
                customId && customIdAvailable !== null
                  ? customIdAvailable
                    ? "green"
                    : "red"
                  : "blue"
              }-500)`
            }}
          />
          {checkingCustomId && (
            <Box
              position="absolute"
              right={3}
              top="50%"
              transform="translateY(-50%)"
            >
              <Icon as={FiLoader} color="gray.500" className="animate-spin" />
            </Box>
          )}
          {!checkingCustomId && customId && customIdAvailable !== null && (
            <Box
              position="absolute"
              right={3}
              top="50%"
              transform="translateY(-50%)"
            >
              <Icon
                as={customIdAvailable ? FiCheck : FiAlertCircle}
                color={customIdAvailable ? "green.500" : "red.500"}
              />
            </Box>
          )}
        </Box>
        <Text
          fontSize="sm"
          color={
            customId && customIdAvailable === false
              ? "red.500"
              : customIdAvailable === true
                ? "green.600"
                : "gray.500"
          }
          mt={1}
        >
          {customId && customIdAvailable === false
            ? validateCustomId(customId) || "This ID is already taken"
            : customIdAvailable === true
              ? "This ID is available!"
              : "Create a memorable URL like /summer-sale"}
        </Text>
      </Box>
    </Box>
  );
};
